import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import StaffLayout from "@/components/StaffLayout";
import { useAuth } from "@/context/AuthContext";
import { api, API_BASE } from "@/lib/api";
import { toast } from "sonner";
import AiGenerationProgress from "@/components/AiGenerationProgress";
import {
  Sparkles, FileText, Link2, Upload, ChevronRight, ChevronLeft, ShieldCheck,
  Loader2, PlusCircle, X, Edit3, Check
} from "lucide-react";

const STEPS = [
  { k: 1, label: "Fuente" },
  { k: 2, label: "Extraer" },
  { k: 3, label: "Contexto" },
  { k: 4, label: "Generar" },
];

export default function AiCourseBuilder() {
  const { user } = useAuth();
  const nav = useNavigate();
  const isSuper = (user?.roles || []).includes("superadmin_dcc");
  const [step, setStep] = useState(1);
  const [busy, setBusy] = useState(false);
  const [sourceType, setSourceType] = useState("text");
  const [pastedText, setPastedText] = useState("");
  const [url, setUrl] = useState("");
  const [rightsConfirmed, setRightsConfirmed] = useState(false);
  const [file, setFile] = useState(null);
  const [extraction, setExtraction] = useState(null);
  const [context, setContext] = useState({
    area_hint: "",
    format_hint: "",
    level: "gerencia_media",
    duration_hours: 40,
    price_target: "",
  });
  const [areas, setAreas] = useState([]);
  const [generation, setGeneration] = useState(null);
  const [publishing, setPublishing] = useState(false);

  useEffect(() => {
    api.get("/catalog/areas").then((r) => setAreas(r.data.items || [])).catch(() => {});
  }, []);

  const goNext = () => setStep((s) => Math.min(4, s + 1));
  const goBack = () => setStep((s) => Math.max(1, s - 1));

  const doExtract = async () => {
    setBusy(true);
    try {
      let res;
      if (sourceType === "document") {
        if (!file) return toast.error("Elige un archivo primero");
        const fd = new FormData();
        fd.append("file", file);
        res = await api.post("/admin/ai-course-builder/extract-file", fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
      } else {
        res = await api.post("/admin/ai-course-builder/extract", {
          source_type: sourceType,
          text: sourceType === "text" ? pastedText : undefined,
          url: sourceType === "url" ? url : undefined,
          rights_confirmed: rightsConfirmed,
        });
      }
      setExtraction(res.data);
      toast.success(`Extraídos ${res.data.char_count.toLocaleString()} caracteres`);
      setStep(2);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error extrayendo contenido");
    } finally {
      setBusy(false);
    }
  };

  const doGenerate = async () => {
    setBusy(true);
    setStep(4);
    try {
      const res = await api.post("/admin/ai-course-builder/generate", {
        extracted_text: extraction.extracted_text,
        area_hint: context.area_hint || undefined,
        format_hint: context.format_hint || undefined,
        level: context.level || undefined,
        duration_hours: parseFloat(context.duration_hours) || undefined,
        price_target: context.price_target ? parseFloat(context.price_target) : undefined,
        source_meta: {
          source_type: extraction.source_type,
          source_url: extraction.source_url,
          source_hash: extraction.source_hash,
          rights_confirmed: rightsConfirmed,
        },
      });
      setGeneration(res.data);
      toast.success(`Draft listo (${res.data.latency_ms}ms)`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error generando");
      setStep(3);
    } finally {
      setBusy(false);
    }
  };

  const doPublish = async () => {
    setPublishing(true);
    try {
      const res = await api.post("/admin/ai-course-builder/publish-draft", {
        generation_id: generation.generation_id,
        draft: generation.draft,
      });
      toast.success(`Programa creado como borrador · ${res.data.code}`);
      nav("/app/staff/catalogo");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error al publicar");
    } finally {
      setPublishing(false);
    }
  };

  return (
    <StaffLayout user={user} superadmin={isSuper}>
      <div className="mb-6">
        <div className="inline-flex items-center gap-2 text-xs uppercase tracking-widest text-[#F26A21] font-bold">
          <Sparkles className="w-4 h-4" /> Fase 9 · AI Course Builder
        </div>
        <h1 className="mt-1 text-3xl font-bold text-white font-display">Generar programa con IA</h1>
        <p className="mt-1 text-sm text-gray-400 max-w-2xl">
          Convierte cualquier temario, sílabo o documento en un programa completo del catálogo DCC.
          Al terminar queda como borrador (no publicado) — completa videos y textos manualmente antes de publicar.
        </p>
      </div>

      {/* Steps rail */}
      <div className="flex items-center gap-2 mb-8 flex-wrap" data-testid="acb-steps">
        {STEPS.map((s) => (
          <div key={s.k} className="flex items-center gap-2">
            <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold ${step >= s.k ? "bg-[#F26A21] text-[#0B0B0B]" : "bg-white/5 text-gray-400"}`}>
              {s.k}
            </div>
            <span className={`text-xs uppercase tracking-widest ${step >= s.k ? "text-white" : "text-gray-500"}`}>{s.label}</span>
            {s.k < STEPS.length && <ChevronRight className="w-3 h-3 text-gray-600" />}
          </div>
        ))}
      </div>

      {step === 1 && (
        <Step1Source
          sourceType={sourceType} setSourceType={setSourceType}
          pastedText={pastedText} setPastedText={setPastedText}
          url={url} setUrl={setUrl}
          rightsConfirmed={rightsConfirmed} setRightsConfirmed={setRightsConfirmed}
          file={file} setFile={setFile}
          onNext={doExtract} busy={busy}
        />
      )}
      {step === 2 && extraction && (
        <Step2Extraction extraction={extraction} setExtraction={setExtraction}
          onBack={goBack} onNext={goNext} />
      )}
      {step === 3 && extraction && (
        <Step3Context context={context} setContext={setContext} areas={areas}
          onBack={goBack} onNext={doGenerate} busy={busy} />
      )}
      {step === 4 && (
        <Step4Result generation={generation} setGeneration={setGeneration}
          busy={busy} publishing={publishing}
          onBack={() => setStep(3)} onPublish={doPublish} />
      )}
    </StaffLayout>
  );
}

function Step1Source({ sourceType, setSourceType, pastedText, setPastedText, url, setUrl,
                      rightsConfirmed, setRightsConfirmed, file, setFile, onNext, busy }) {
  return (
    <div data-testid="acb-step1" className="max-w-3xl">
      <p className="text-[10px] uppercase tracking-widest text-gray-500 font-semibold mb-3">Elige la fuente</p>
      <div className="grid sm:grid-cols-3 gap-3 mb-6">
        {[
          { k: "text", i: FileText, l: "Pegar texto" },
          { k: "document", i: Upload, l: "Subir DOCX/PDF" },
          { k: "url", i: Link2, l: "URL pública" },
        ].map((o) => {
          const Icon = o.i;
          return (
            <button key={o.k} onClick={() => setSourceType(o.k)}
              data-testid={`acb-source-${o.k}`}
              className={`p-4 rounded-2xl border transition-all min-h-[80px] flex flex-col items-start gap-2 ${
                sourceType === o.k ? "bg-[#F26A21]/10 border-[#F26A21] text-white" : "bg-white/5 border-white/10 text-gray-300"
              }`}>
              <Icon className="w-5 h-5" />
              <span className="font-semibold text-sm">{o.l}</span>
            </button>
          );
        })}
      </div>

      {sourceType === "text" && (
        <div>
          <label className="text-[10px] uppercase tracking-widest text-gray-500 font-semibold">Pega el temario / sílabo / notas</label>
          <textarea value={pastedText} onChange={(e) => setPastedText(e.target.value)}
            rows={12} data-testid="acb-text-input"
            placeholder="Módulo 1: Introducción a...&#10;Objetivos:...&#10;Audiencia:..."
            className="w-full mt-1 bg-white/5 border border-white/10 rounded-md px-3 py-2 text-sm text-white font-mono focus:outline-none focus:border-[#F26A21]" />
          <p className="mt-1 text-xs text-gray-500">{pastedText.length.toLocaleString()} caracteres</p>
        </div>
      )}

      {sourceType === "document" && (
        <div>
          <label className="text-[10px] uppercase tracking-widest text-gray-500 font-semibold">DOCX / PDF / TXT (máx 5 MB)</label>
          <input type="file" accept=".docx,.pdf,.txt"
            onChange={(e) => setFile(e.target.files?.[0] || null)}
            data-testid="acb-file-input"
            className="w-full mt-1 bg-white/5 border border-white/10 rounded-md px-3 py-2 text-sm text-white" />
          {file && <p className="mt-1 text-xs text-emerald-300">{file.name} · {(file.size / 1024).toFixed(1)} KB</p>}
        </div>
      )}

      {sourceType === "url" && (
        <div>
          <label className="text-[10px] uppercase tracking-widest text-gray-500 font-semibold">URL de contenido público</label>
          <input value={url} onChange={(e) => setUrl(e.target.value)}
            placeholder="https://tu-sitio.com/temario.html"
            data-testid="acb-url-input"
            className="w-full mt-1 bg-white/5 border border-white/10 rounded-md px-3 py-2 text-sm text-white font-mono" />
          <label className="mt-4 flex items-start gap-3 p-4 rounded-lg bg-amber-500/10 border border-amber-500/30 cursor-pointer">
            <input type="checkbox" checked={rightsConfirmed} onChange={(e) => setRightsConfirmed(e.target.checked)}
              data-testid="acb-rights-checkbox" className="mt-1" />
            <span className="text-xs text-amber-200 leading-relaxed">
              <ShieldCheck className="w-3 h-3 inline mr-1" />
              Confirmo tener derechos sobre este contenido o autorización explícita del propietario.
              Dominios institucionales (abm.com.mx, bmv.com.mx, cnbv.gob.mx) están bloqueados.
            </span>
          </label>
        </div>
      )}

      <div className="mt-6 flex justify-end">
        <button onClick={onNext} disabled={busy}
          data-testid="acb-step1-next-btn"
          className="inline-flex items-center gap-2 px-6 py-3 rounded-md bg-[#F26A21] hover:bg-[#D9541A] disabled:opacity-50 text-[#0B0B0B] font-semibold text-sm min-h-[44px]">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <ChevronRight className="w-4 h-4" />}
          {busy ? "Extrayendo…" : "Extraer contenido"}
        </button>
      </div>
    </div>
  );
}

function Step2Extraction({ extraction, setExtraction, onBack, onNext }) {
  return (
    <div data-testid="acb-step2" className="max-w-4xl">
      <div className="flex items-start justify-between mb-3">
        <div>
          <p className="text-[10px] uppercase tracking-widest text-gray-500 font-semibold">Texto extraído · editable</p>
          <p className="text-xs text-gray-400 mt-1">
            {extraction.char_count.toLocaleString()} caracteres · ~{extraction.estimated_tokens.toLocaleString()} tokens
          </p>
        </div>
      </div>
      <textarea
        value={extraction.extracted_text}
        onChange={(e) => setExtraction({ ...extraction, extracted_text: e.target.value })}
        rows={16}
        data-testid="acb-extraction-textarea"
        className="w-full bg-white/5 border border-white/10 rounded-md px-3 py-2 text-xs text-white font-mono focus:outline-none focus:border-[#F26A21]" />
      <div className="mt-6 flex justify-between">
        <button onClick={onBack} className="px-4 py-2 rounded-md border border-white/10 text-gray-300 text-sm min-h-[44px]">
          <ChevronLeft className="w-4 h-4 inline mr-1" />Atrás
        </button>
        <button onClick={onNext}
          data-testid="acb-step2-next-btn"
          className="px-6 py-3 rounded-md bg-[#F26A21] hover:bg-[#D9541A] text-[#0B0B0B] font-semibold text-sm min-h-[44px]">
          <ChevronRight className="w-4 h-4 inline mr-1" />Configurar contexto
        </button>
      </div>
    </div>
  );
}

function Step3Context({ context, setContext, areas, onBack, onNext, busy }) {
  const set = (k, v) => setContext({ ...context, [k]: v });
  return (
    <div data-testid="acb-step3" className="max-w-2xl">
      <p className="text-[10px] uppercase tracking-widest text-gray-500 font-semibold mb-4">Ajusta parámetros del programa</p>
      <div className="space-y-4">
        <div>
          <label className="text-[10px] uppercase tracking-widest text-gray-500 font-semibold">Área objetivo</label>
          <select value={context.area_hint} onChange={(e) => set("area_hint", e.target.value)}
            data-testid="acb-area-select"
            className="w-full mt-1 bg-white/5 border border-white/10 rounded-md px-3 py-2 text-sm text-white">
            <option value="">— Que la IA elija —</option>
            {areas.map((a) => <option key={a.slug} value={a.slug}>{a.name}</option>)}
          </select>
        </div>
        <div className="grid sm:grid-cols-2 gap-3">
          <div>
            <label className="text-[10px] uppercase tracking-widest text-gray-500 font-semibold">Formato</label>
            <select value={context.format_hint} onChange={(e) => set("format_hint", e.target.value)}
              data-testid="acb-format-select"
              className="w-full mt-1 bg-white/5 border border-white/10 rounded-md px-3 py-2 text-sm text-white">
              <option value="">— Auto —</option>
              {["curso", "diplomado", "programa", "seminario", "certificacion", "masterclass"].map((f) =>
                <option key={f} value={f}>{f}</option>
              )}
            </select>
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-widest text-gray-500 font-semibold">Nivel audiencia</label>
            <select value={context.level} onChange={(e) => set("level", e.target.value)}
              data-testid="acb-level-select"
              className="w-full mt-1 bg-white/5 border border-white/10 rounded-md px-3 py-2 text-sm text-white">
              {["analistas", "gerencia_media", "alta_direccion", "consejo"].map((l) =>
                <option key={l} value={l}>{l}</option>
              )}
            </select>
          </div>
        </div>
        <div className="grid sm:grid-cols-2 gap-3">
          <div>
            <label className="text-[10px] uppercase tracking-widest text-gray-500 font-semibold">Duración (horas)</label>
            <input value={context.duration_hours} onChange={(e) => set("duration_hours", e.target.value)}
              type="number" data-testid="acb-duration-input"
              className="w-full mt-1 bg-white/5 border border-white/10 rounded-md px-3 py-2 text-sm text-white" />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-widest text-gray-500 font-semibold">Precio target MXN (opcional)</label>
            <input value={context.price_target} onChange={(e) => set("price_target", e.target.value)}
              type="number" placeholder="40000"
              data-testid="acb-price-input"
              className="w-full mt-1 bg-white/5 border border-white/10 rounded-md px-3 py-2 text-sm text-white" />
          </div>
        </div>
      </div>
      <div className="mt-6 flex justify-between">
        <button onClick={onBack} className="px-4 py-2 rounded-md border border-white/10 text-gray-300 text-sm min-h-[44px]">
          <ChevronLeft className="w-4 h-4 inline mr-1" />Atrás
        </button>
        <button onClick={onNext} disabled={busy}
          data-testid="acb-step3-generate-btn"
          className="inline-flex items-center gap-2 px-6 py-3 rounded-md bg-[#F26A21] hover:bg-[#D9541A] disabled:opacity-50 text-[#0B0B0B] font-semibold text-sm min-h-[44px]">
          <Sparkles className="w-4 h-4" />Generar con IA
        </button>
      </div>
    </div>
  );
}

function Step4Result({ generation, setGeneration, busy, publishing, onBack, onPublish }) {
  if (busy || !generation) {
    return (
      <div className="max-w-2xl">
        <AiGenerationProgress />
      </div>
    );
  }
  const d = generation.draft;
  const setDraft = (patch) => setGeneration({ ...generation, draft: { ...d, ...patch } });
  const updateModule = (idx, patch) => {
    const modules = [...(d.modules || [])];
    modules[idx] = { ...modules[idx], ...patch };
    setDraft({ modules });
  };
  const updateLesson = (mi, li, patch) => {
    const modules = [...(d.modules || [])];
    const lessons = [...(modules[mi].lessons || [])];
    lessons[li] = { ...lessons[li], ...patch };
    modules[mi] = { ...modules[mi], lessons };
    setDraft({ modules });
  };
  return (
    <div data-testid="acb-step4" className="max-w-4xl">
      <div className="mb-6">
        <p className="text-[10px] uppercase tracking-widest text-[#F26A21] font-semibold">Draft generado</p>
        <p className="text-xs text-gray-500 mt-1">Editables: título, descripción, módulos, lecciones. Al publicar quedará como borrador.</p>
      </div>
      <div className="p-5 rounded-2xl bg-white/5 border border-white/10 space-y-4">
        <div>
          <label className="text-[10px] uppercase tracking-widest text-gray-500 font-semibold">Título</label>
          <input value={d.title} onChange={(e) => setDraft({ title: e.target.value })}
            data-testid="acb-draft-title"
            className="w-full mt-1 bg-black/30 border border-white/10 rounded-md px-3 py-2 text-lg font-bold text-white" />
        </div>
        <div>
          <label className="text-[10px] uppercase tracking-widest text-gray-500 font-semibold">Descripción de marketing</label>
          <textarea value={d.marketing_description} onChange={(e) => setDraft({ marketing_description: e.target.value })}
            rows={4} data-testid="acb-draft-description"
            className="w-full mt-1 bg-black/30 border border-white/10 rounded-md px-3 py-2 text-sm text-white" />
        </div>
        <div className="grid sm:grid-cols-3 gap-3">
          <Chip label="Área" value={d.recommended_area_slug} />
          <Chip label="Formato" value={d.recommended_format} />
          <Chip label="Duración" value={d.recommended_duration_text} />
          <Chip label="Precio" value={`$${(d.recommended_price_mxn || 0).toLocaleString()} MXN`} />
          <Chip label="IADA aval" value={d.iada_aval_available ? "Sí" : "No"} />
          <Chip label="Tags" value={(d.tags || []).slice(0, 3).join(", ")} />
        </div>
        <details className="rounded-lg border border-white/5 p-3" open>
          <summary className="cursor-pointer text-xs uppercase tracking-widest text-gray-400 font-semibold">
            Objetivos de aprendizaje ({(d.learning_objectives || []).length})
          </summary>
          <ul className="mt-2 space-y-1 text-xs text-gray-300">
            {(d.learning_objectives || []).map((o, i) => <li key={i}>· {o}</li>)}
          </ul>
        </details>
        <details className="rounded-lg border border-white/5 p-3">
          <summary className="cursor-pointer text-xs uppercase tracking-widest text-gray-400 font-semibold">
            Módulos ({(d.modules || []).length}) · Total lecciones: {(d.modules || []).reduce((a, m) => a + (m.lessons || []).length, 0)}
          </summary>
          <div className="mt-3 space-y-3">
            {(d.modules || []).map((m, mi) => (
              <div key={mi} className="p-3 rounded bg-black/30 border border-white/5" data-testid={`acb-module-${mi}`}>
                <input value={m.title} onChange={(e) => updateModule(mi, { title: e.target.value })}
                  className="w-full bg-transparent text-sm font-bold text-white focus:outline-none" />
                <p className="text-[10px] text-gray-500">{m.estimated_hours}h · {(m.lessons || []).length} lecciones</p>
                <ul className="mt-2 space-y-1 text-xs text-gray-300">
                  {(m.lessons || []).map((l, li) => (
                    <li key={li} className="flex items-center gap-2">
                      <span className="text-[9px] uppercase tracking-widest text-[#F26A21]">{l.type}</span>
                      <input value={l.title} onChange={(e) => updateLesson(mi, li, { title: e.target.value })}
                        className="flex-1 bg-transparent text-xs text-gray-300 focus:outline-none" />
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </details>
      </div>
      <div className="mt-6 flex justify-between gap-3">
        <button onClick={onBack} className="px-4 py-2 rounded-md border border-white/10 text-gray-300 text-sm min-h-[44px]">
          <ChevronLeft className="w-4 h-4 inline mr-1" />Regenerar
        </button>
        <button onClick={onPublish} disabled={publishing}
          data-testid="acb-publish-btn"
          className="inline-flex items-center gap-2 px-6 py-3 rounded-md bg-[#F26A21] hover:bg-[#D9541A] disabled:opacity-50 text-[#0B0B0B] font-semibold text-sm min-h-[44px]">
          <Check className="w-4 h-4" /> {publishing ? "Publicando…" : "Publicar como borrador"}
        </button>
      </div>
    </div>
  );
}

function Chip({ label, value }) {
  return (
    <div className="p-3 rounded bg-black/30 border border-white/5">
      <p className="text-[9px] uppercase tracking-widest text-gray-500 font-semibold">{label}</p>
      <p className="mt-1 text-sm text-white truncate">{value || "—"}</p>
    </div>
  );
}
